import React, { useEffect, useState, useCallback } from 'react';
import { Warning, ArrowsClockwise, X } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

/**
 * Top-of-app banner that surfaces broken / stale Outlook sync.
 *
 * Renders nothing when health.severity === 'ok'. Polls every 60s so
 * Mat sees recovery the moment it happens. Severity drives the colour:
 *   - error  → red banner, primary action "Reconnect Outlook"
 *   - warn   → amber banner, primary action "Sync now"
 */
export default function SyncHealthBanner() {
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/admin/sync/health');
      setHealth(r.data);
      // Auto-undismiss when severity changes (e.g. recovered → broken again)
      setDismissed((prev) => prev && r.data?.severity !== 'ok' ? prev : false);
    } catch {
      /* keep silent — don't add noise on top of existing failure */
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!health || health.severity === 'ok' || dismissed) return null;

  const isError = health.severity === 'error';
  const palette = isError
    ? {
        bg: 'bg-red-500/15 border-red-500/40',
        text: 'text-red-700',
        accent: 'text-red-600',
        cta: 'bg-red-500 hover:bg-red-400 text-zinc-900',
      }
    : {
        bg: 'bg-amber-500/10 border-amber-500/40',
        text: 'text-amber-900',
        accent: 'text-amber-600',
        cta: 'bg-amber-500 hover:bg-amber-400 text-white',
      };

  let headline = 'Outlook sync needs attention';
  let detail = health.last_sync_message || 'Tap to view details.';
  let primaryLabel = 'Open sync settings';
  let primaryAction = () => navigate('/admin/settings');

  const reconnectFlow = async () => {
    setBusy(true);
    try {
      try {
        await api.post('/outlook/disconnect');
      } catch {
        /* ignore — proceed to auth-url anyway */
      }
      const origin = encodeURIComponent(window.location.origin);
      const res = await api.get(`/outlook/auth-url?origin=${origin}`);
      window.location.href = res.data.auth_url;
    } catch (err) {
      // Fall back: send Mat to Settings so he can see the Azure-keys setup
      navigate('/admin/settings');
    } finally {
      setBusy(false);
    }
  };

  if (health.state === 'not_configured') {
    headline = 'Azure credentials missing — Outlook sync cannot run';
    detail = 'Add AZURE_CLIENT_ID, AZURE_CLIENT_SECRET, AZURE_TENANT_ID to backend/.env, then restart the backend.';
    primaryLabel = 'Open setup';
  } else if (health.state === 'not_connected') {
    headline = 'Outlook is not connected';
    detail = 'Lender emails are not being ingested. Connect Outlook to start the 5-minute auto-sync.';
    primaryLabel = 'Connect Outlook';
    primaryAction = reconnectFlow;
  } else if (health.state === 'broken') {
    headline = 'Outlook sync is broken';
    detail = health.last_sync_message
      ? `Last error: ${health.last_sync_message.slice(0, 200)}`
      : 'The last sync failed.';
    primaryLabel = 'Reconnect Outlook';
    primaryAction = reconnectFlow;
  } else if (health.state === 'stale') {
    const mins = health.minutes_since_last_success ?? '?';
    headline = `Sync is stale — last successful sync ${mins} min ago`;
    detail = 'Trigger a manual sync to confirm Outlook is still reachable.';
    primaryLabel = 'Sync now';
    primaryAction = async () => {
      setBusy(true);
      try {
        await api.post('/outlook/sync-now?since_days=3');
        setTimeout(refresh, 4000);
      } finally {
        setBusy(false);
      }
    };
  }

  return (
    <div
      data-testid="sync-health-banner"
      className={`border-b ${palette.bg}`}
    >
      <div className="px-5 lg:px-8 py-3 flex items-start gap-3 flex-wrap">
        <Warning size={20} weight="fill" className={`${palette.accent} mt-0.5 shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${palette.text}`} data-testid="sync-health-headline">
            {headline}
          </div>
          <div className={`text-[12px] mt-0.5 ${palette.text} opacity-80 break-words`} data-testid="sync-health-detail">
            {detail}
          </div>
          {health.emails_today != null && (
            <div className={`text-[11px] mt-1 font-data ${palette.text} opacity-70`}>
              Emails today: <span className="font-bold">{health.emails_today}</span>
              {' · '}last hour: <span className="font-bold">{health.emails_last_hour}</span>
              {health.minutes_since_last_sync != null && <>{' · '}last attempt: <span className="font-bold">{health.minutes_since_last_sync}m ago</span></>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={primaryAction}
            disabled={busy}
            data-testid="sync-health-primary-action"
            className={`text-xs px-3 py-1.5 rounded-sm font-medium disabled:opacity-50 ${palette.cta} flex items-center gap-1.5`}
          >
            <ArrowsClockwise size={14} className={busy ? 'animate-spin' : ''} />
            {busy ? 'Working…' : primaryLabel}
          </button>
          <button
            onClick={() => setDismissed(true)}
            data-testid="sync-health-dismiss"
            title="Hide until next change"
            className={`p-1.5 rounded-sm hover:bg-zinc-900/10 ${palette.text}`}
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
